import { ApiMap } from '../../environments/environment.interface';

/**
 * Runtime API URL override registry (dev / PR only).
 *
 * Single source of truth for which backend APIs can be repointed at runtime via
 * the "Override APIs" popup in the shared layout footer. The override service,
 * the dialog, and the `resolveApiUrlOverride` HTTP chokepoint hook all read this
 * registry — adding an API here is the ONLY code change needed to make it
 * overridable.
 *
 * Stg/Prod URLs are written as `process.env['Urls:*@stg'|'@prod']` tokens. The
 * Angular build fetches ALL Azure App Configuration keys regardless of
 * `--configuration`, so webpack's DefinePlugin bakes BOTH the real stg AND prod
 * `*.universalplant.com` URLs into every build (see frontend-config.md). PR URLs
 * are built from `prSlug` because PR app services are ephemeral and not in Azure
 * config.
 *
 * ── How to add a new overridable API ───────────────────────────────────────
 * (e.g. when an API gains a monorepo PR deployment, give it a `prSlug` so the
 *  PR# target appears in the dialog.)
 *  1. Ensure the API key exists in `ApiUrlID` (environment.interface.ts) and is
 *     populated in each app's `environment.*.ts` `urls` map.
 *  2. Add ONE entry to `API_URL_OVERRIDE_REGISTRY` below:
 *       - `localUrl`     ← the API's Properties/launchSettings.json applicationUrl.
 *       - `stgUrl`/`prodUrl` ← `process.env['Urls:<Key>@stg'|'@prod']` tokens.
 *         Both labels must exist in Azure App Config. Omit a token if the API is
 *         not in Azure config → that target falls back to Custom-only.
 *       - `prSlug`       ← the AppServiceName (minus the `{PR#}-` prefix) from
 *         deployment-pipelines/config/<api>.ts. OMIT if the API has no PR
 *         deployment → the PR# option is hidden for it.
 *  3. No other code changes — the dialog, all agent channels, the gating, and the
 *     `resolveApiUrlOverride` chokepoint all read this registry automatically.
 * ────────────────────────────────────────────────────────────────────────────
 */

/** Key into `environment.urls` (i.e. `ApiUrlID`). */
export type OverridableApiKey = keyof ApiMap;

/** Where an API can be pointed. `pr` carries a number; anything else is a custom URL. */
export type ApiUrlTargetKind =
  | 'default'
  | 'local'
  | 'stg'
  | 'prod'
  | 'pr'
  | 'custom';

export interface ApiUrlOverrideRegistryEntry {
  /** Key into `environment.urls` — also identifies the current build's default URL. */
  apiKey: OverridableApiKey;
  /** Human-friendly label shown in the dialog and the override banner. */
  label: string;
  /** Local dev URL from the API's launchSettings.json. */
  localUrl: string;
  /** Real stg URL (Azure config token). Omit if the API is not in Azure config. */
  stgUrl?: string;
  /** Real prod URL (Azure config token). Omit if the API is not in Azure config. */
  prodUrl?: string;
  /** azurewebsites slug (minus `{PR#}-`) for PR app services. Omit if no PR deploy. */
  prSlug?: string;
}

export const API_URL_OVERRIDE_REGISTRY: ApiUrlOverrideRegistryEntry[] = [
  {
    apiKey: 'azureAPI',
    label: 'Azure API',
    localUrl: 'https://localhost:10094',
    stgUrl: process.env['Urls:AzureApi@stg'],
    prodUrl: process.env['Urls:AzureApi@prod'],
    prSlug: 'azure-api-upsllc',
  },
  {
    apiKey: 'spartaAPI',
    label: 'Sparta API',
    localUrl: 'https://localhost:10091',
    stgUrl: process.env['Urls:SpartaApi@stg'],
    prodUrl: process.env['Urls:SpartaApi@prod'],
    prSlug: 'sparta-api-upsllc',
  },
  {
    apiKey: 'viewpointAPI',
    label: 'Viewpoint API',
    localUrl: 'http://localhost:10090',
    stgUrl: process.env['Urls:ViewpointApi@stg'],
    prodUrl: process.env['Urls:ViewpointApi@prod'],
    prSlug: 'vp-api-upsllc',
  },
  {
    apiKey: 'dynamicAPI',
    label: 'Dynamic Content API',
    localUrl: 'https://localhost:5003',
    stgUrl: process.env['Urls:DynamicApi@stg'],
    prodUrl: process.env['Urls:DynamicApi@prod'],
    prSlug: 'dynamic-content-api-upsllc',
  },
  {
    apiKey: 'softwareSecurityAPI',
    label: 'Software Security API',
    localUrl: 'https://localhost:7068',
    stgUrl: process.env['Urls:SoftwareSecurityApi@stg'],
    prodUrl: process.env['Urls:SoftwareSecurityApi@prod'],
    prSlug: 'security-api-upsllc',
  },
  {
    apiKey: 'toolTrackerAPI',
    label: 'Tool Tracker API',
    localUrl: 'https://localhost:5002',
    stgUrl: process.env['Urls:ToolTrackerApi@stg'],
    prodUrl: process.env['Urls:ToolTrackerApi@prod'],
    prSlug: 'tool-tracker-api-upsllc',
  },
  {
    // Employee Packet API — migrated into the monorepo (apps/employee-packet-api).
    // localUrl ← UPS.EmployeePacket.API/Properties/launchSettings.json applicationUrl.
    // prSlug ← deployment-pipelines/config/employee-packet-api.ts AppServiceName minus `{PR#}-`.
    apiKey: 'packetAPI',
    label: 'Employee Packet API',
    localUrl: 'https://localhost:10096',
    stgUrl: process.env['Urls:PacketApi@stg'],
    prodUrl: process.env['Urls:PacketApi@prod'],
    prSlug: 'employee-packet-api-upsllc',
  },
];

/** Lookup a registry entry by its API key. */
export function getRegistryEntry(
  apiKey: OverridableApiKey
): ApiUrlOverrideRegistryEntry | undefined {
  return API_URL_OVERRIDE_REGISTRY.find((e) => e.apiKey === apiKey);
}
